import { useState } from 'react'
import { Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import LeagueFooter from '../components/LeagueFooter'
import DivisionPills from '../components/DivisionPills'
import { getImageUrl, type Player } from '../../../../services/api'
import { getTeamDisplay } from '../../../../utils/teamDisplay'
import type { PlayersPageVM } from '../../../core/pages/players/types'
import { CLASSIC_SELECT_CHEVRON } from '../components/selectChevron'
import { sf } from '../../../core/features/sf'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

// Classic view for the `players` page; data lives in
// core/pages/players/controller.ts. Grid/list is view state.

export default function Players({ vm }: { vm: PlayersPageVM }) {
  const m = useT(siteClassicMessages).players.players
  const core = useT(siteCoreMessages)
  const tenantName = vm.leagueName
  const players = vm.items.map((p) => p.raw)
  const total = vm.paged.total
  const loading = vm.status === 'loading' || vm.refreshing
  const loadingMore = vm.paged.loadingMore ?? false
  const error = vm.error
  const filterStatus = vm.playerStatus.value
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')

  const handleLoadMore = () => vm.paged.loadMore?.()
  const hasMore = vm.hasMore

  const clearFilters = vm.clearFilters

  const hasFilters = vm.hasFilters

  return (
    <div className="min-h-screen bg-gradient-to-b from-secondary-dark via-bg-primary to-bg-primary">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-8 pb-12 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-primary/50"></div>
              <span className="text-primary text-[10px] uppercase tracking-[0.25em] font-bold">
                {tenantName}
              </span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-primary/50"></div>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Montserrat'] font-black text-white uppercase tracking-tight mb-4">
              {core.pages.nav.players}
            </h1>
            <p className="text-white/50 text-sm sm:text-base max-w-xl mx-auto">
              {m.subtitle}
            </p>
            {/* #583: division pill row (renders nothing without 2+ live divisions) */}
            <DivisionPills division={vm.division} className="mt-6" center />
          </div>

          {/* Search & Filters */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-bg-secondary/60 backdrop-blur-xl border border-secondary/30 p-4 sm:p-6">
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-primary/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={m.search}
                  value={vm.search.value}
                  onChange={(e) => vm.search.set(e.target.value)}
                  {...sf('players.search')}
                  className="w-full pl-12 pr-4 py-3.5 bg-bg-primary/40 border border-secondary/40 text-white placeholder-white/30 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-all text-base leading-5 sm:text-sm"
                />
              </div>

              {/* Status Toggle */}
              <div className="flex mb-3 bg-bg-primary/30 p-0.5 border border-secondary/30" {...sf('players.filter.status')}>
                {vm.playerStatus.options.map(s => (
                  <button
                    key={s.value}
                    onClick={() => vm.playerStatus.set(s.value)}
                    className={`flex-1 px-3 py-2 text-[10px] uppercase tracking-wider font-bold transition-all ${
                      filterStatus === s.value
                        ? 'bg-primary text-black'
                        : 'text-white/50 hover:text-white'
                    }`}
                    {...sf('players.filter.status', s.value)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Filter Row */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1" {...sf('players.filter.team')}>
                  <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-white/50 mb-2">{m.team}</label>
                  <select
                    aria-label={m.team}
                    value={vm.team.value != null ? String(vm.team.value) : ''}
                    onChange={(e) => vm.team.set(e.target.value ? parseInt(e.target.value, 10) : null)}
                    className="w-full pl-4 pr-10 py-2.5 bg-bg-primary/40 border border-secondary/40 text-white focus:outline-none focus:border-primary/50 transition-all text-base leading-5 sm:text-sm appearance-none cursor-pointer truncate"
                    style={CLASSIC_SELECT_CHEVRON}
                  >
                    <option value="">{m.allTeams}</option>
                    {vm.team.options.map((team) => (
                      <option key={team.value} value={String(team.value)}>{team.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1" {...sf('players.filter.position')}>
                  <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-white/50 mb-2">{m.position}</label>
                  <select
                    aria-label={m.position}
                    value={vm.position.value ?? ''}
                    onChange={(e) => vm.position.set(e.target.value || null)}
                    className="w-full pl-4 pr-10 py-2.5 bg-bg-primary/40 border border-secondary/40 text-white focus:outline-none focus:border-primary/50 transition-all text-base leading-5 sm:text-sm appearance-none cursor-pointer truncate"
                    style={CLASSIC_SELECT_CHEVRON}
                  >
                    <option value="">{m.allPositions}</option>
                    {vm.position.options.map((pos) => (
                      <option key={pos.value} value={pos.value ?? ''}>{pos.label}</option>
                    ))}
                  </select>
                </div>
                {hasFilters && (
                  <div className="flex items-end">
                    <button
                      onClick={clearFilters}
                      className="px-4 py-2.5 bg-transparent border border-secondary/50 text-white/60 hover:text-white hover:border-white/40 transition-all text-sm"
                      {...sf('players.clear')}
                    >
                      {m.clear}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Players Section */}
      <section className="relative pb-24">
        <div className="container mx-auto px-4">
          {/* Results Count + View Toggle */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-primary/30"></div>
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/50" {...sf('players.count')}>
                {loading ? core.common.loading : m.count(total)}
              </span>
            </div>
            <div className="flex bg-bg-primary/30 border border-secondary/30 p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 transition-all ${viewMode === 'grid' ? 'bg-primary text-black' : 'text-white/50 hover:text-white'}`}
                aria-label={m.gridView}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M1 2.5A1.5 1.5 0 012.5 1h3A1.5 1.5 0 017 2.5v3A1.5 1.5 0 015.5 7h-3A1.5 1.5 0 011 5.5v-3zm8 0A1.5 1.5 0 0110.5 1h3A1.5 1.5 0 0115 2.5v3A1.5 1.5 0 0113.5 7h-3A1.5 1.5 0 019 5.5v-3zm-8 8A1.5 1.5 0 012.5 9h3A1.5 1.5 0 017 10.5v3A1.5 1.5 0 015.5 15h-3A1.5 1.5 0 011 13.5v-3zm8 0A1.5 1.5 0 0110.5 9h3a1.5 1.5 0 011.5 1.5v3a1.5 1.5 0 01-1.5 1.5h-3A1.5 1.5 0 019 13.5v-3z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 transition-all ${viewMode === 'list' ? 'bg-primary text-black' : 'text-white/50 hover:text-white'}`}
                aria-label={m.listView}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M2.5 12a.5.5 0 01.5-.5h10a.5.5 0 010 1H3a.5.5 0 01-.5-.5zm0-4a.5.5 0 01.5-.5h10a.5.5 0 010 1H3a.5.5 0 01-.5-.5zm0-4a.5.5 0 01.5-.5h10a.5.5 0 010 1H3a.5.5 0 01-.5-.5z" />
                </svg>
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-900/20 border border-red-500/30 p-6 text-center mb-8" {...sf('state.error')}>
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className={viewMode === 'grid'
              ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3'
              : 'space-y-3'
            } {...sf('state.loading')}>
              {[...Array(12)].map((_, i) => (
                <div key={i} className="animate-pulse bg-bg-secondary/60 border border-secondary/30 rounded-lg p-4">
                  {viewMode === 'grid' ? (
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-20 h-20 bg-white/10 rounded-lg"></div>
                      <div className="h-3 bg-white/10 rounded w-2/3"></div>
                      <div className="h-2 bg-white/5 rounded w-1/2"></div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-white/10 rounded-lg flex-shrink-0"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-white/10 rounded w-1/3"></div>
                        <div className="h-3 bg-white/5 rounded w-1/4"></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && players.length === 0 && (
            <div className="text-center py-20" {...sf('players.empty')}>
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-secondary/20 border border-secondary/30 mb-6">
                <svg className="w-10 h-10 text-primary/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-['Montserrat'] font-black text-white uppercase tracking-tight mb-2">
                {hasFilters ? m.noneFound : m.noneYet}
              </h3>
              <p className="text-white/50 text-sm mb-6">{hasFilters ? m.tryAdjusting : m.rostersEmpty}</p>
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-secondary/30 border border-secondary text-white hover:bg-secondary/50 hover:border-primary/50 transition-all text-sm font-bold uppercase tracking-wider"
                >
                  {m.clearFilters}
                </button>
              )}
            </div>
          )}

          {/* Grid View / List View */}
          {!loading && !error && players.length > 0 && (vm.groups ? (
            // #583: "All divisions": one heading per division (a player rostered in two is listed under each).
            <div className="space-y-12" {...sf('players.list')}>
              {vm.groups.map((group) => {
                const key = group.division?.id ?? 'none'
                const items = group.items.map((p) => p.raw)
                return (
                  <section key={key} aria-labelledby={`players-division-${key}`}>
                    <h2 id={`players-division-${key}`} className="mb-5 text-xl sm:text-2xl font-['Montserrat'] font-black text-white uppercase tracking-tight">
                      {group.division?.name ?? m.otherPlayers}
                    </h2>
                    {viewMode === 'grid' ? <PlayersGrid players={items} marker={false} /> : <PlayersList players={items} marker={false} />}
                  </section>
                )
              })}
            </div>
          ) : viewMode === 'grid' ? (
            <PlayersGrid players={players} />
          ) : (
            <PlayersList players={players} />
          ))}

          {/* Load More */}
          {!loading && hasMore && (
            <div className="text-center mt-10">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                {...sf('players.load-more')}
                className="px-8 py-3 bg-secondary/30 border border-secondary/50 text-white font-bold uppercase tracking-wider text-sm hover:bg-secondary/50 hover:border-primary/50 transition-all disabled:opacity-50"
              >
                {loadingMore ? core.common.loading : m.loadMore(players.length, total)}
              </button>
            </div>
          )}
        </div>
      </section>

      <LeagueFooter />
    </div>
  )
}

function formatHeight(inches: number | null | undefined): string {
  if (!inches) return ''
  const feet = Math.floor(inches / 12)
  const remainingInches = inches % 12
  return `${feet}'${remainingInches}"`
}

// Grid view cards (unchanged markup), for the whole list or one division group.
function PlayersGrid({ players, marker = true }: { players: Player[]; marker?: boolean }) {
  const silhouette = useT(siteClassicMessages).players.players.silhouette
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" {...(marker ? sf('players.list') : {})}>
      {players.map((player) => (
        <Link
          key={player.id}
          to={`/players/${player.id}`}
          className="group block bg-bg-secondary/60 border border-secondary/30 hover:border-primary/50 hover:bg-bg-secondary/80 transition-all duration-300 rounded-lg overflow-hidden"
        >
          <div className="flex flex-col items-center p-4">
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gradient-to-b from-bg-secondary to-bg-secondary border border-secondary/40 mb-3">
              {player.photo_url ? (
                <img
                  src={getImageUrl(player.photo_url) || ''}
                  alt={`${player.first_name} ${player.last_name}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.style.display = 'none'
                    const fallback = target.nextElementSibling as HTMLElement
                    if (fallback) fallback.style.display = 'flex'
                  }}
                />
              ) : null}
              <div
                className={`absolute inset-0 ${player.photo_url ? 'hidden' : 'flex'} items-center justify-center`}
                style={{ display: player.photo_url ? 'none' : 'flex' }}
              >
                <img
                  src="/default-player.svg"
                  alt={silhouette}
                  className="w-3/4 h-3/4 object-contain opacity-20"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
              {player.jersey_number != null && (
                <div className="absolute -top-1 -right-1 bg-bg-primary/90 backdrop-blur-sm border border-primary/40 px-1.5 py-0.5 rounded shadow-lg">
                  <span className="text-primary font-['Montserrat'] font-black text-[10px]">
                    #{player.jersey_number}
                  </span>
                </div>
              )}
            </div>
            <h3 className="font-['Montserrat'] font-black text-white text-xs sm:text-sm group-hover:text-primary transition-colors text-center leading-tight">
              {player.first_name} {player.last_name}
            </h3>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap justify-center">
              {player.position && (
                <span className="text-[9px] uppercase tracking-[0.15em] font-bold text-white/70 bg-secondary/60 px-1.5 py-0.5 rounded">
                  {player.position}
                </span>
              )}
              {player.team_short_code && (
                <span className="text-[9px] uppercase tracking-wider text-white/50">
                  {player.team_short_code}
                </span>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}

// List view rows (unchanged markup), for the whole list or one division group.
function PlayersList({ players, marker = true }: { players: Player[]; marker?: boolean }) {
  const silhouette = useT(siteClassicMessages).players.players.silhouette
  return (
    <div className="space-y-2" {...(marker ? sf('players.list') : {})}>
      {players.map((player) => (
        <Link
          key={player.id}
          to={`/players/${player.id}`}
          className="group block bg-bg-secondary/60 border border-secondary/30 hover:border-primary/50 hover:bg-bg-secondary/80 transition-all duration-300 rounded-lg overflow-hidden"
        >
          <div className="flex items-center gap-4 p-4">
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gradient-to-b from-bg-secondary to-bg-secondary border border-secondary/40">
              {player.photo_url ? (
                <img
                  src={getImageUrl(player.photo_url) || ''}
                  alt={`${player.first_name} ${player.last_name}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement
                    target.style.display = 'none'
                    const fallback = target.nextElementSibling as HTMLElement
                    if (fallback) fallback.style.display = 'flex'
                  }}
                />
              ) : null}
              <div
                className={`absolute inset-0 ${player.photo_url ? 'hidden' : 'flex'} items-center justify-center`}
                style={{ display: player.photo_url ? 'none' : 'flex' }}
              >
                <img
                  src="/default-player.svg"
                  alt={silhouette}
                  className="w-3/4 h-3/4 object-contain opacity-20"
                  style={{ filter: 'brightness(0) invert(1)' }}
                />
              </div>
              {player.jersey_number != null && (
                <div className="absolute -top-1 -right-1 bg-bg-primary/90 backdrop-blur-sm border border-primary/40 px-1.5 py-0.5 rounded shadow-lg">
                  <span className="text-primary font-['Montserrat'] font-black text-[10px]">
                    #{player.jersey_number}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-['Montserrat'] font-black text-white text-base sm:text-lg group-hover:text-primary transition-colors truncate">
                  {player.first_name} {player.last_name}
                </h3>
                {player.nickname && (
                  <span className="text-primary/80 text-xs font-semibold hidden sm:inline">
                    "{player.nickname}"
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-white/60">
                {player.position && (
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-white/70 bg-secondary/60 px-2 py-0.5 rounded">
                    {player.position}
                  </span>
                )}
                {player.team_name && (
                  <div className="flex items-center gap-1.5">
                    {player.team_logo_url && (
                      <img src={getImageUrl(player.team_logo_url) || ''} alt={player.team_name} className="w-4 h-4 object-contain" />
                    )}
                    <span className="text-xs uppercase tracking-wider">
                      {getTeamDisplay({ name: player.team_name, short_code: player.team_short_code }, 'short')}
                    </span>
                  </div>
                )}
                {player.height_inches && (
                  <span className="text-xs">{formatHeight(player.height_inches)}</span>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 text-white/30 group-hover:text-primary transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </Link>
      ))}
    </div>
  )
}
